import { useCallback, useEffect, useState } from 'react';

import {
  getVapidPublicKey,
  subscribePush,
  unsubscribePush,
} from '../services/pushSubscriptionService';

const SW_PATH = '/push-sw.js';

type PushSubscriptionState = {
  isSupported: boolean;
  permissionState: NotificationPermission;
  isSubscribed: boolean;
  isLoading: boolean;
  subscribe: () => Promise<void>;
  unsubscribe: () => Promise<void>;
};

function isPushSupported(): boolean {
  return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
}

function urlBase64ToUint8Array(base64String: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; i++) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray.buffer as ArrayBuffer;
}

export function usePushSubscription(): PushSubscriptionState {
  const [permissionState, setPermissionState] = useState<NotificationPermission>(
    isPushSupported() ? Notification.permission : 'default'
  );
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isSupported = isPushSupported();

  const refreshState = useCallback(() => {
    if (!isSupported) return;
    setPermissionState(Notification.permission);
    navigator.serviceWorker
      .getRegistration(SW_PATH)
      .then(reg => reg?.pushManager.getSubscription())
      .then(sub => setIsSubscribed(!!sub));
  }, [isSupported]);

  const subscribe = useCallback(async () => {
    if (!isSupported) return;
    setIsLoading(true);

    try {
      const permission = await Notification.requestPermission();
      setPermissionState(permission);
      if (permission !== 'granted') return;

      const vapidPublicKey = await getVapidPublicKey();
      const reg = await navigator.serviceWorker.register(SW_PATH);

      if (!reg.active) {
        const sw = reg.installing ?? reg.waiting;
        if (sw) {
          await new Promise<void>(resolve => {
            sw.addEventListener('statechange', () => {
              if (sw.state === 'activated') resolve();
            });
          });
        }
      }

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey),
      });

      await subscribePush(subscription);
      setIsSubscribed(true);
    } catch (err) {
      console.error('Push subscription failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  const unsubscribe = useCallback(async () => {
    if (!isSupported) return;
    setIsLoading(true);

    try {
      const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
      const subscription = await reg?.pushManager.getSubscription();
      if (subscription) {
        await subscription.unsubscribe();
        await unsubscribePush(subscription.endpoint);
      }
      setIsSubscribed(false);
    } catch (err) {
      console.error('Push unsubscribe failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isSupported]);

  useEffect(() => {
    refreshState();
    navigator.permissions.query({ name: 'notifications' }).then(status => {
      const onChange = () => {
        refreshState();
        if (status.state === 'granted') subscribe();
      };
      status.addEventListener('change', onChange);
    });
  }, [refreshState, subscribe]);

  return { isSupported, permissionState, isSubscribed, isLoading, subscribe, unsubscribe };
}
